/**
 * Feedback Redux Slice
 *
 * Manages feedback state for inline feedback form display and submission.
 * Tracks form visibility, submission state, and user feedback text.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { FeedbackType } from '../../types/feedback';

export interface FeedbackState {
  /** Whether feedback mode is currently active */
  isActive: boolean;

  /** The type of feedback being collected (positive/negative) */
  feedbackType: FeedbackType | null;

  /** Message ID that feedback is being collected for */
  messageId: string | null;

  /** Whether the inline feedback form should be shown */
  showForm: boolean;

  /** The feedback text entered by the user */
  feedbackText: string;

  /** Whether feedback has been successfully submitted */
  isSubmitted: boolean;

  /** Whether feedback is currently being submitted */
  isSubmitting: boolean;

  /** The chat/session ID to submit feedback for (saved before session reset) */
  feedbackChatId: string | null;
}

const initialState: FeedbackState = {
  isActive: false,
  feedbackType: null,
  messageId: null,
  showForm: false,
  feedbackText: '',
  isSubmitted: false,
  isSubmitting: false,
  feedbackChatId: null,
};

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    startFeedback: (
      state,
      action: PayloadAction<{
        messageId: string;
        feedbackType: FeedbackType;
        feedbackChatId: string;
      }>,
    ) => {
      state.isActive = true;
      state.feedbackType = action.payload.feedbackType;
      state.messageId = action.payload.messageId;
      state.feedbackChatId = action.payload.feedbackChatId;
      state.showForm = true;
      state.feedbackText = '';
      state.isSubmitted = false;
      state.isSubmitting = false;
    },

    setFeedbackText: (state, action: PayloadAction<string>) => {
      state.feedbackText = action.payload;
    },

    startSubmitting: (state) => {
      state.isSubmitting = true;
    },

    submitFeedbackSuccess: (state) => {
      state.isSubmitted = true;
      state.isSubmitting = false;
      state.showForm = false;
    },

    submitFeedbackError: (state) => {
      state.isSubmitting = false;
    },

    resetFeedback: () => initialState,
  },
});

export const {
  startFeedback,
  setFeedbackText,
  startSubmitting,
  submitFeedbackSuccess,
  submitFeedbackError,
  resetFeedback,
} = feedbackSlice.actions;

export default feedbackSlice.reducer;

export const selectIsActive = (state: { feedback: FeedbackState }) =>
  state.feedback.isActive;
export const selectFeedbackType = (state: { feedback: FeedbackState }) =>
  state.feedback.feedbackType;
export const selectMessageId = (state: { feedback: FeedbackState }) =>
  state.feedback.messageId;
export const selectShowForm = (state: { feedback: FeedbackState }) =>
  state.feedback.showForm;
export const selectFeedbackText = (state: { feedback: FeedbackState }) =>
  state.feedback.feedbackText;
export const selectIsSubmitted = (state: { feedback: FeedbackState }) =>
  state.feedback.isSubmitted;
export const selectIsSubmitting = (state: { feedback: FeedbackState }) =>
  state.feedback.isSubmitting;
export const selectFeedbackChatId = (state: { feedback: FeedbackState }) =>
  state.feedback.feedbackChatId;
